import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { handoversApi, clientsApi, shiftsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Loader2, X, ClipboardList, Clock } from 'lucide-react';
import { LogoHome } from '../components/LogoHome';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

export default function HandoverPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [handovers, setHandovers] = useState([]);
  
  // Form state
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedShift, setSelectedShift] = useState('');
  const [summary, setSummary] = useState('');
  const [keyPoints, setKeyPoints] = useState(['']);
  const [followUps, setFollowUps] = useState(['']);
  const [concerns, setConcerns] = useState(['']);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsRes, shiftsRes, handoversRes] = await Promise.all([
        clientsApi.getAll(),
        shiftsApi.getAll(),
        handoversApi.getAll()
      ]);
      setClients(clientsRes.data);
      setShifts(shiftsRes.data);
      setHandovers(handoversRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleArrayItemChange = (setter, index, value) => {
    setter(prev => {
      const newArr = [...prev];
      newArr[index] = value;
      return newArr;
    });
  };

  const addArrayItem = (setter) => {
    setter(prev => [...prev, '']);
  };

  const removeArrayItem = (setter, index) => {
    setter(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!selectedClient || !summary || keyPoints.filter(k => k.trim()).length === 0) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      await handoversApi.create({
        client_id: selectedClient,
        shift_id: selectedShift || null,
        outgoing_worker_id: user.id,
        summary,
        key_points: keyPoints.filter(k => k.trim()),
        follow_ups: followUps.filter(f => f.trim()),
        concerns: concerns.filter(c => c.trim())
      });
      toast.success('Handover created successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to create handover');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="handover-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <h1 className="font-heading font-semibold text-slate-900">Shift Handover</h1>
            </div>
            <LogoHome className="h-12 w-auto" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-teal-600" />
                  Create Handover
                </CardTitle>
                <CardDescription>Document important information for the next worker</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Client & Shift Selection */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Client <span className="text-red-500">*</span></Label>
                    <Select value={selectedClient} onValueChange={setSelectedClient}>
                      <SelectTrigger className="h-12" data-testid="handover-client">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Related Shift (Optional)</Label>
                    <Select value={selectedShift} onValueChange={setSelectedShift}>
                      <SelectTrigger className="h-12" data-testid="handover-shift">
                        <SelectValue placeholder="Select shift" />
                      </SelectTrigger>
                      <SelectContent>
                        {shifts.map((shift) => (
                          <SelectItem key={shift.id} value={shift.id}>
                            {shift.shift_date} - {shift.start_time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Summary */}
                <div className="space-y-2">
                  <Label>Summary <span className="text-red-500">*</span></Label>
                  <Textarea
                    placeholder="Provide a brief overview of the shift..."
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    rows={4}
                    data-testid="handover-summary"
                  />
                </div>

                {/* Key Points */}
                <div className="space-y-3">
                  <Label>Key Points <span className="text-red-500">*</span></Label>
                  {keyPoints.map((point, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        placeholder={`Key point ${index + 1}`}
                        value={point}
                        onChange={(e) => handleArrayItemChange(setKeyPoints, index, e.target.value)}
                        className="h-12"
                        data-testid={`key-point-${index}`}
                      />
                      {keyPoints.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeArrayItem(setKeyPoints, index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addArrayItem(setKeyPoints)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Key Point
                  </Button>
                </div>

                {/* Follow-ups */}
                <div className="space-y-3">
                  <Label>Follow-up Tasks</Label>
                  {followUps.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        placeholder={`Follow-up ${index + 1}`}
                        value={item}
                        onChange={(e) => handleArrayItemChange(setFollowUps, index, e.target.value)}
                        className="h-12"
                        data-testid={`follow-up-${index}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeArrayItem(setFollowUps, index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addArrayItem(setFollowUps)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Follow-up
                  </Button>
                </div>

                {/* Concerns */}
                <div className="space-y-3">
                  <Label>Concerns / Alerts</Label>
                  {concerns.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        placeholder={`Concern ${index + 1}`}
                        value={item}
                        onChange={(e) => handleArrayItemChange(setConcerns, index, e.target.value)}
                        className="h-12"
                        data-testid={`concern-${index}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeArrayItem(setConcerns, index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addArrayItem(setConcerns)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Concern
                  </Button>
                </div>

                {/* Submit */}
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full h-12 bg-teal-600 hover:bg-teal-700"
                  data-testid="submit-handover-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Create Handover
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Recent Handovers */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-base">Recent Handovers</CardTitle>
              </CardHeader>
              <CardContent>
                {handovers.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Clock className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">No handovers yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {handovers.slice(0, 5).map((handover) => {
                      const client = clients.find(c => c.id === handover.client_id);
                      return (
                        <div key={handover.id} className="p-3 rounded-lg border border-slate-200 hover:border-teal-500/50 transition-colors">
                          <div className="flex justify-between items-start mb-2">
                            <p className="font-medium text-slate-900 text-sm">{client?.name || 'Unknown Client'}</p>
                            <Badge variant="secondary" className="text-xs">
                              {new Date(handover.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-600 line-clamp-2">{handover.summary}</p>
                          {handover.concerns?.length > 0 && (
                            <Badge variant="destructive" className="mt-2 text-xs">
                              {handover.concerns.length} concern(s)
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
